import type { TemplateWidget } from '@/constants';

/**
 * The grid the icon is drawn against. `aspectRatio` is the on-screen width / height of the whole
 * grid; without it, one column is as wide as one row is tall.
 */
export type TemplateIconExtent = {
  columns: number;
  rows: number;
  aspectRatio?: number;
};

/**
 * Every icon is normalised to this viewBox width, so gap, radius and index text stay the same
 * share of the icon whether the grid has 16 or 48 columns.
 */
export const VIEWBOX_WIDTH = 160;
export const GAP = 2.4;
/** Fallback extent for small templates, matching the original 16 × 12 minimum. */
const MIN_COLUMNS = 16;
const MIN_ROWS = 12;

export type TemplateIconGeometry = {
  unitWidth: number;
  unitHeight: number;
  viewBoxHeight: number;
  /** Size of the extent (the screen) in viewBox units, including the outer gap. */
  frameWidth: number;
  frameHeight: number;
  /** How many times wider / taller the drawn area is than the extent. */
  widthRatio: number;
  heightRatio: number;
  /** Some widget reaches past the extent's right or bottom edge. */
  overflows: boolean;
};

/**
 * Lays the widgets out in viewBox units. With `clipToExtent`, the viewBox is the extent alone and
 * widgets past it are cut off; otherwise it grows to include every widget.
 */
export const getTemplateIconGeometry = ({
  widgets,
  extent,
  clipToExtent = false,
}: {
  widgets: TemplateWidget[];
  extent?: TemplateIconExtent | undefined;
  clipToExtent?: boolean;
}): TemplateIconGeometry => {
  const columns = extent?.columns ?? MIN_COLUMNS;
  const rows = extent?.rows ?? MIN_ROWS;
  const contentX = Math.max(0, ...widgets.map(w => w.x + w.w));
  const contentY = Math.max(0, ...widgets.map(w => w.y + w.h));
  const overflows = extent !== undefined && (contentX > columns || contentY > rows);

  const maxX = clipToExtent ? columns : Math.max(contentX, columns);
  const maxY = clipToExtent ? rows : Math.max(contentY, rows);

  const unitWidth = (VIEWBOX_WIDTH - GAP) / maxX;
  // The extent keeps its screen shape; widgets past it continue at the same unit size.
  const unitHeight = extent?.aspectRatio
    ? (columns * unitWidth) / extent.aspectRatio / rows
    : unitWidth;

  return {
    unitWidth,
    unitHeight,
    viewBoxHeight: maxY * unitHeight + GAP,
    frameWidth: columns * unitWidth + GAP,
    frameHeight: rows * unitHeight + GAP,
    widthRatio: maxX / columns,
    heightRatio: maxY / rows,
    overflows,
  };
};
